<template>
    <div class="-m-4 sm:-m-6 lg:-mx-8 h-[calc(100vh-24px)] flex flex-col">
        <div class="shrink-0 px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-4 bg-gray-50">
            <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                    <UButton to="/transports" color="gray" variant="ghost" icon="i-ph-arrow-left-bold" />
                    <h1 class="text-2xl font-semibold text-gray-700">{{ isEditing ? 'Edit Transport' : 'New Transport Request' }}</h1>
                </div>
                <div class="flex items-center space-x-2">
                    <UButton to="/transports" size="lg" color="white">Cancel</UButton>
                    <UButton @click="form?.submit()" size="lg" color="black" :loading="loading">{{ isEditing ? 'Save' : 'Create' }}</UButton>
                </div>
            </div>  
        </div>

        <UForm ref="form" @submit="onSubmit" :schema="schema" :state="state" :validate-on="['submit']" class="flex-1 overflow-auto px-4 sm:px-6 lg:px-8 pt-2 pb-12">
            <div class="flex gap-6 max-w-7xl mx-auto items-start">
                <div class="flex-1 space-y-6">
                    <UFormGroup name="trip_type">
                    <div class="grid grid-cols-2 gap-4">
                        <button
                            type="button"
                            @click="state.trip_type = TransportTripType.oneWay"
                            class="bg-white ring-1 rounded-lg shadow-sm px-5 py-4 text-left transition-colors"
                            :class="state.trip_type === TransportTripType.oneWay ? 'ring-primary-500 ring-2' : 'ring-black/5'"
                        >
                            <div class="flex items-start space-x-3">
                                <div class="pt-0.5">
                                    <div class="size-4 rounded-full border-2 flex items-center justify-center" :class="state.trip_type === TransportTripType.oneWay ? 'border-primary-500' : 'border-gray-300'">
                                        <div v-if="state.trip_type === TransportTripType.oneWay" class="size-2 rounded-full bg-primary-500" />
                                    </div>
                                </div>
                                <div>
                                    <p class="text-sm font-semibold text-gray-800">One Way</p>
                                    <p class="text-xs text-gray-500 mt-0.5">Single trip from pickup to dropoff.</p>
                                </div>
                            </div>
                        </button>
                        <button
                            type="button"
                            @click="state.trip_type = TransportTripType.roundTrip"
                            class="bg-white ring-1 rounded-lg shadow-sm px-5 py-4 text-left transition-colors"
                            :class="state.trip_type === TransportTripType.roundTrip ? 'ring-primary-500 ring-2' : 'ring-black/5'"
                        >
                            <div class="flex items-start space-x-3">
                                <div class="pt-0.5">
                                    <div class="size-4 rounded-full border-2 flex items-center justify-center" :class="state.trip_type === TransportTripType.roundTrip ? 'border-primary-500' : 'border-gray-300'">
                                        <div v-if="state.trip_type === TransportTripType.roundTrip" class="size-2 rounded-full bg-primary-500" />
                                    </div>
                                </div>
                                <div>
                                    <p class="text-sm font-semibold text-gray-800">Round Trip</p>
                                    <p class="text-xs text-gray-500 mt-0.5">Pickup to dropoff, then return.</p>
                                </div>
                            </div>
                        </button>
                    </div>
                    </UFormGroup>

                    <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm">
                        <div class="flex gap-10 px-6 py-5">
                            <div class="w-48 shrink-0 pt-1">
                                <h4 class="text-sm font-semibold text-gray-800">Provider & Customer</h4>
                            </div>
                            <div class="flex-1 space-y-4">
                                <UFormGroup name="provider_id" label="Provider" required>
                                    <USelectMenu
                                        v-model="state.provider_id"
                                        :options="providers"
                                        value-attribute="id"
                                        option-attribute="name"
                                        placeholder="Select a provider..."
                                        size="xl"
                                    />
                                </UFormGroup>
                                <UFormGroup name="customer_id" label="Customer" required>
                                    <div v-if="selectedCustomer" class="flex items-center justify-between rounded-md border border-gray-300 px-3 py-2.5">
                                        <div class="flex items-center space-x-3">
                                            <Avatar size="sm" :path="selectedCustomer.avatar" :placeholder="[selectedCustomer.first_name, selectedCustomer.last_name].filter(Boolean).join(' ')" />
                                            <div class="leading-4">
                                                <p class="text-sm font-medium">{{ [selectedCustomer.first_name, selectedCustomer.middle_name, selectedCustomer.last_name].filter(Boolean).join(' ') }}</p>
                                                <span class="text-xs text-gray-500 capitalize">{{ [selectedCustomer.gender, age(selectedCustomer.birth_date), selectedCustomer.blood_type].filter(Boolean).join(' · ') }}</span>
                                            </div>
                                        </div>
                                        <UButton icon="i-ph-x-bold" color="gray" variant="ghost" size="xs" @click="clearCustomer" />
                                    </div>
                                    <div v-else class="relative">
                                        <UInput
                                            v-model="customerSearch"
                                            size="xl"
                                            placeholder="Search by name or phone..."
                                            icon="i-ph-magnifying-glass-duotone"
                                            :loading="searchingCustomers"
                                            @focus="customerDropdownOpen = true"
                                            @blur="onCustomerBlur"
                                        />
                                        <div v-if="customerDropdownOpen && customerSearch && !searchingCustomers" class="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg ring-1 ring-black/5 max-h-80 overflow-y-auto">
                                            <div v-if="!customerResults.length" class="p-4 text-center">
                                                <p class="text-sm text-gray-500">No results found.</p>
                                                <UButton @mousedown.prevent="creatingCustomer = true" icon="i-ph-plus-bold" label="New Customer" color="black" size="sm" class="mt-2" />
                                            </div>
                                            <template v-else>
                                                <button
                                                    v-for="customer in customerResults"
                                                    :key="customer.id"
                                                    type="button"
                                                    @mousedown.prevent="selectCustomer(customer)"
                                                    class="w-full px-4 py-3 space-y-2 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0"
                                                >
                                                    <div class="flex items-center space-x-3">
                                                        <Avatar class="shrink-0" size="sm" :path="customer.avatar" :placeholder="[customer.first_name, customer.last_name].filter(Boolean).join(' ')" />
                                                        <div class="flex-1 leading-4">
                                                            <p class="font-medium text-sm">{{ [customer.first_name, customer.middle_name, customer.last_name].filter(Boolean).join(' ') }}</p>
                                                            <span class="text-xs text-gray-500 capitalize">{{ [customer.gender, age(customer.birth_date), customer.blood_type].filter(Boolean).join(' · ') }}</span>
                                                        </div>
                                                    </div>
                                                    <div class="space-y-0.5 text-sm">
                                                        <Field v-if="customer.subscription_id" inline label="Subscription" label-class="text-[11px] uppercase text-gray-400">
                                                            <BadgeSubscriptionStatus with-expiry :subscription="customer.subscriptions?.find((s) => s.id === customer.subscription_id)" />
                                                        </Field>
                                                        <Field v-if="customer.phone" inline label="Phone" label-class="text-[11px] uppercase text-gray-400">
                                                            <PhoneLabel :value="customer.phone" />
                                                        </Field>
                                                    </div>
                                                </button>
                                                <button type="button" @mousedown.prevent="creatingCustomer = true" class="w-full px-4 py-3 text-left text-sm font-medium text-primary hover:bg-gray-50 transition-colors flex items-center space-x-2 border-t border-gray-200">
                                                    <UIcon name="i-ph-plus-bold" class="size-4" />
                                                    <span>Create a new customer</span>
                                                </button>
                                            </template>
                                        </div>
                                    </div>
                                </UFormGroup>
                            </div>
                        </div>
                    </div>

                    <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm">
                        <div class="flex gap-10 px-6 py-5">
                            <div class="w-48 shrink-0 pt-1">
                                <h4 class="text-sm font-semibold text-gray-800">Pickup</h4>
                                <p class="text-sm text-gray-500 mt-1">Where the customer will be picked up.</p>
                            </div>
                            <div class="flex-1 space-y-4">
                                <div class="flex space-x-2">
                                    <UButton :color="state.pickup_type === 'medical_facility' ? 'primary' : 'white'" size="sm" @click="state.pickup_type = 'medical_facility'">Medical Facility</UButton>
                                    <UButton :color="state.pickup_type === 'residential' ? 'primary' : 'white'" size="sm" @click="state.pickup_type = 'residential'">Residential Address</UButton>
                                </div>
                                <UFormGroup name="departure_address.lat" label="Location" required>
                                    <div v-if="!pickupSelected" class="relative">
                                        <UInput
                                            v-model="pickupSearch"
                                            size="xl"
                                            placeholder="Search address..."
                                            icon="i-ph-magnifying-glass-duotone"
                                            :loading="searchingPickup"
                                            @focus="pickupDropdownOpen = true"
                                            @blur="pickupDropdownOpen = false"
                                        />
                                        <div v-if="pickupDropdownOpen && (pickupPlaces.length || pickupPredictions.length)" class="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg ring-1 ring-black/5 max-h-64 overflow-y-auto">
                                            <template v-if="pickupPlaces.length">
                                                <div class="px-3 py-1.5 text-[11px] font-semibold text-gray-400 uppercase bg-gray-50">Provider Places</div>
                                                <button
                                                    v-for="place in pickupPlaces"
                                                    :key="place.id"
                                                    type="button"
                                                    @mousedown.prevent="selectPickupPlace(place)"
                                                    class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between gap-2"
                                                >
                                                    <div class="flex items-center gap-2 min-w-0">
                                                        <UBadge color="primary" variant="subtle" size="xs" label="Provider" />
                                                        <span class="truncate">{{ place.name }}</span>
                                                    </div>
                                                    <span v-if="place.one_way_price != null" class="text-xs text-gray-400 font-mono shrink-0">${{ place.one_way_price }} / ${{ place.round_trip_price }}</span>
                                                    <span v-else class="text-xs text-gray-400 shrink-0">Base</span>
                                                </button>
                                            </template>
                                            <template v-if="pickupPredictions.length">
                                                <div class="px-3 py-1.5 text-[11px] font-semibold text-gray-400 uppercase bg-gray-50">Suggestions</div>
                                                <button
                                                    v-for="pred in pickupPredictions"
                                                    :key="pred.placeId"
                                                    type="button"
                                                    @mousedown.prevent="selectPickupPrediction(pred)"
                                                    class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                                                >
                                                    <div class="font-medium text-gray-800 truncate">{{ pred.mainText }}</div>
                                                    <div v-if="pred.secondaryText" class="text-xs text-gray-500 truncate">{{ pred.secondaryText }}</div>
                                                </button>
                                            </template>
                                        </div>
                                    </div>
                                    <div v-else class="flex items-start gap-3 px-3 py-2.5 bg-white rounded-md border border-gray-300 hover:border-gray-400 transition-colors">
                                        <UIcon name="i-ph-map-pin-fill" class="text-primary-500 shrink-0 mt-0.5 size-5" />
                                        <div class="flex-1 min-w-0 space-y-0.5 text-sm">
                                            <div class="font-medium text-gray-800">{{ pickupLine1 }}</div>
                                            <div v-if="pickupCity" class="text-primary-600 font-medium text-xs">{{ pickupCity }}</div>
                                            <div v-if="pickupRegion || pickupCountry" class="text-gray-500 text-xs">
                                                <span v-if="pickupRegion">{{ pickupRegion }}</span>
                                                <span v-if="pickupRegion && pickupCountry"> · </span>
                                                <span v-if="pickupCountry">{{ pickupCountry }}</span>
                                            </div>
                                            <div v-if="pickupLat && pickupLng" class="text-gray-400 font-mono text-xs">{{ pickupLat.toFixed(5) }}, {{ pickupLng.toFixed(5) }}</div>
                                        </div>
                                        <UButton icon="i-ph-x-bold" color="gray" variant="ghost" size="xs" @click="clearPickup" />
                                    </div>
                                </UFormGroup>
                                <template v-if="state.pickup_type === 'medical_facility'">
                                    <UFormGroup label="Facility Name">
                                        <UInput v-model="state.departure_address.name" type="text" size="xl" />
                                    </UFormGroup>
                                    <div class="grid grid-cols-2 gap-4">
                                        <UFormGroup label="Department">
                                            <UInput v-model="state.departure_address.department" type="text" size="xl" />
                                        </UFormGroup>
                                        <UFormGroup label="Room">
                                            <UInput v-model="state.departure_address.room" type="text" size="xl" />
                                        </UFormGroup>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="grid grid-cols-2 gap-4">
                                        <UFormGroup label="City">
                                            <UInput v-model="state.departure_address.city" type="text" size="xl" />
                                        </UFormGroup>
                                        <UFormGroup label="Street">
                                            <UInput v-model="state.departure_address.street" type="text" size="xl" />
                                        </UFormGroup>
                                    </div>
                                    <div class="grid grid-cols-2 gap-4">
                                        <UFormGroup label="Building">
                                            <UInput v-model="state.departure_address.building" type="text" size="xl" />
                                        </UFormGroup>
                                        <UFormGroup label="Floor">
                                            <UInput v-model="state.departure_address.floor" type="text" size="xl" />
                                        </UFormGroup>
                                    </div>
                                    <div class="flex items-center space-x-6">
                                        <UCheckbox v-model="state.departure_address.narrow_stairs" label="Narrow stairs" />
                                        <UCheckbox v-model="state.departure_address.no_electricity" label="No electricity" />
                                        <UCheckbox v-model="state.departure_address.no_elevator" label="No elevator" />
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>

                    <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm">
                        <div class="flex gap-10 px-6 py-5">
                            <div class="w-48 shrink-0 pt-1">
                                <h4 class="text-sm font-semibold text-gray-800">Dropoff</h4>
                                <p class="text-sm text-gray-500 mt-1">Where the customer will be dropped off.</p>
                            </div>
                            <div class="flex-1 space-y-4">
                                <div class="flex space-x-2">
                                    <UButton :color="state.dropoff_type === 'medical_facility' ? 'primary' : 'white'" size="sm" @click="state.dropoff_type = 'medical_facility'">Medical Facility</UButton>
                                    <UButton :color="state.dropoff_type === 'residential' ? 'primary' : 'white'" size="sm" @click="state.dropoff_type = 'residential'">Residential Address</UButton>
                                </div>
                                <UFormGroup name="arrival_address.lat" label="Location" required>
                                    <div v-if="!dropoffSelected" class="relative">
                                        <UInput
                                            v-model="dropoffSearch"
                                            size="xl"
                                            placeholder="Search address..."
                                            icon="i-ph-magnifying-glass-duotone"
                                            :loading="searchingDropoff"
                                            @focus="dropoffDropdownOpen = true"
                                            @blur="dropoffDropdownOpen = false"
                                        />
                                        <div v-if="dropoffDropdownOpen && (dropoffPlaces.length || dropoffPredictions.length)" class="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg ring-1 ring-black/5 max-h-64 overflow-y-auto">
                                            <template v-if="dropoffPlaces.length">
                                                <div class="px-3 py-1.5 text-[11px] font-semibold text-gray-400 uppercase bg-gray-50">Provider Places</div>
                                                <button
                                                    v-for="place in dropoffPlaces"
                                                    :key="place.id"
                                                    type="button"
                                                    @mousedown.prevent="selectDropoffPlace(place)"
                                                    class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between gap-2"
                                                >
                                                    <div class="flex items-center gap-2 min-w-0">
                                                        <UBadge color="primary" variant="subtle" size="xs" label="Provider" />
                                                        <span class="truncate">{{ place.name }}</span>
                                                    </div>
                                                    <span v-if="place.one_way_price != null" class="text-xs text-gray-400 font-mono shrink-0">${{ place.one_way_price }} / ${{ place.round_trip_price }}</span>
                                                    <span v-else class="text-xs text-gray-400 shrink-0">Base</span>
                                                </button>
                                            </template>
                                            <template v-if="dropoffPredictions.length">
                                                <div class="px-3 py-1.5 text-[11px] font-semibold text-gray-400 uppercase bg-gray-50">Suggestions</div>
                                                <button
                                                    v-for="pred in dropoffPredictions"
                                                    :key="pred.placeId"
                                                    type="button"
                                                    @mousedown.prevent="selectDropoffPrediction(pred)"
                                                    class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                                                >
                                                    <div class="font-medium text-gray-800 truncate">{{ pred.mainText }}</div>
                                                    <div v-if="pred.secondaryText" class="text-xs text-gray-500 truncate">{{ pred.secondaryText }}</div>
                                                </button>
                                            </template>
                                        </div>
                                    </div>
                                    <div v-else class="flex items-start gap-3 px-3 py-2.5 bg-white rounded-md border border-gray-300 hover:border-gray-400 transition-colors">
                                        <UIcon name="i-ph-map-pin-fill" class="text-primary-500 shrink-0 mt-0.5 size-5" />
                                        <div class="flex-1 min-w-0 space-y-0.5 text-sm">
                                            <div class="font-medium text-gray-800">{{ dropoffLine1 }}</div>
                                            <div v-if="dropoffCity" class="text-primary-600 font-medium text-xs">{{ dropoffCity }}</div>
                                            <div v-if="dropoffRegion || dropoffCountry" class="text-gray-500 text-xs">
                                                <span v-if="dropoffRegion">{{ dropoffRegion }}</span>
                                                <span v-if="dropoffRegion && dropoffCountry"> · </span>
                                                <span v-if="dropoffCountry">{{ dropoffCountry }}</span>
                                            </div>
                                            <div v-if="dropoffLat && dropoffLng" class="text-gray-400 font-mono text-xs">{{ dropoffLat.toFixed(5) }}, {{ dropoffLng.toFixed(5) }}</div>
                                        </div>
                                        <UButton icon="i-ph-x-bold" color="gray" variant="ghost" size="xs" @click="clearDropoff" />
                                    </div>
                                </UFormGroup>
                                <template v-if="state.dropoff_type === 'medical_facility'">
                                    <UFormGroup label="Facility Name">
                                        <UInput v-model="state.arrival_address.name" type="text" size="xl" />
                                    </UFormGroup>
                                    <div class="grid grid-cols-2 gap-4">
                                        <UFormGroup label="Department">
                                            <UInput v-model="state.arrival_address.department" type="text" size="xl" />
                                        </UFormGroup>
                                        <UFormGroup label="Room">
                                            <UInput v-model="state.arrival_address.room" type="text" size="xl" />
                                        </UFormGroup>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="grid grid-cols-2 gap-4">
                                        <UFormGroup label="City">
                                            <UInput v-model="state.arrival_address.city" type="text" size="xl" />
                                        </UFormGroup>
                                        <UFormGroup label="Street">
                                            <UInput v-model="state.arrival_address.street" type="text" size="xl" />
                                        </UFormGroup>
                                    </div>
                                    <div class="grid grid-cols-2 gap-4">
                                        <UFormGroup label="Building">
                                            <UInput v-model="state.arrival_address.building" type="text" size="xl" />
                                        </UFormGroup>
                                        <UFormGroup label="Floor">
                                            <UInput v-model="state.arrival_address.floor" type="text" size="xl" />
                                        </UFormGroup>
                                    </div>
                                    <div class="flex items-center space-x-6">
                                        <UCheckbox v-model="state.arrival_address.narrow_stairs" label="Narrow stairs" />
                                        <UCheckbox v-model="state.arrival_address.no_electricity" label="No electricity" />
                                        <UCheckbox v-model="state.arrival_address.no_elevator" label="No elevator" />
                                    </div>
                                </template>
                                <p v-if="state.trip_type === TransportTripType.roundTrip" class="text-xs text-gray-500 bg-gray-50 rounded px-3 py-2">
                                    Return trip (Dropoff → Pickup) is included in the round-trip fee.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                        <div class="flex gap-10 px-6 py-5">
                            <div class="w-48 shrink-0 pt-1">
                                <h4 class="text-sm font-semibold text-gray-800">Transport Details</h4>
                            </div>
                            <div class="flex-1 space-y-4">
                                <UFormGroup name="reason" label="Reason" required>
                                    <USelectMenu v-model="state.reason" :options="reasonOptions" value-attribute="value" option-attribute="name" placeholder="Select reason..." size="xl" />
                                </UFormGroup>
                                <UFormGroup name="reason_details" label="Details">
                                    <UInput v-model="state.reason_details" type="text" size="xl" placeholder="e.g. Cardiology consultation..." />
                                </UFormGroup>
                                <UFormGroup v-if="state.reason && state.reason !== TransportReason.returnHome" name="appointment_time" label="Appointment Time">
                                    <UInput v-model="state.appointment_time" type="time" size="xl" />
                                </UFormGroup>
                                <div class="grid grid-cols-2 gap-4">
                                    <UFormGroup name="posture" label="Posture">
                                        <USelectMenu v-model="state.posture" :options="postureOptions" value-attribute="value" option-attribute="name" placeholder="Select..." size="xl" />
                                    </UFormGroup>
                                    <UFormGroup name="mobility" label="Mobility">
                                        <USelectMenu v-model="state.mobility" :options="mobilityOptions" value-attribute="value" option-attribute="name" placeholder="Select..." size="xl" />
                                    </UFormGroup>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="w-96 shrink-0 sticky top-0">
                    <div class="space-y-6">
                        <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm p-5 space-y-3">
                            <h4 class="text-sm font-semibold text-gray-800">Estimated Fee</h4>
                            <template v-if="!feeReady && !feeOverride">
                                <div class="text-sm text-gray-400">Select provider, trip type, pickup, and dropoff to calculate fee.</div>
                            </template>
                            <template v-else-if="calculatingFee && !feeOverride">
                                <div class="flex items-center space-x-2 text-sm text-gray-400">
                                    <UIcon name="i-ph-circle-notch-bold" class="size-4 animate-spin" />
                                    <span>Calculating...</span>
                                </div>
                            </template>
                            <template v-else-if="estimatedFee != null && !feeOverride">
                                <div v-if="feeSource === 'place'" class="text-xs text-gray-500">Provider place pricing</div>
                                <div v-else-if="feeSource === 'local'" class="text-xs text-gray-500">Local area pricing</div>
                                <div v-else-if="feeDistance != null" class="text-xs text-gray-500 space-y-0.5">
                                    <p>Distance: {{ feeDistance }} km · Zone {{ feeZone?.zone_number }}</p>
                                    <p v-if="feeFirstLeg">Includes ${{ feeFirstLeg }} first-leg fee</p>
                                </div>
                                <div class="text-2xl font-bold font-mono">${{ estimatedFee }}</div>
                            </template>
                            <template v-else-if="feeReady && !feeOverride">
                                <div class="text-sm text-gray-400">No matching pricing zone found.</div>
                            </template>
                            <UCheckbox v-model="feeOverride" label="Override manually" />
                            <UInput v-if="feeOverride" v-model.number="manualFee" type="number" size="lg" placeholder="Enter fee..." icon="i-ph-currency-dollar-duotone" />
                            <div v-if="feeOverride && manualFee" class="text-2xl font-bold font-mono">${{ manualFee }}</div>
                        </div>

                        <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                            <div class="px-5 py-4 space-y-4">
                                <h4 class="text-sm font-semibold text-gray-800">Schedule</h4>
                                <UFormGroup name="scheduled_date" label="Date" required>
                                    <UInput v-model="state.scheduled_date" type="date" size="lg" :min="new Date().toISOString().split('T')[0]" />
                                </UFormGroup>
                                <UFormGroup name="scheduled_time" label="Time" required>
                                    <UInput v-model="state.scheduled_time" type="time" size="lg" />
                                </UFormGroup>
                            </div>
                        </div>

                        <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                            <div class="px-5 py-4 space-y-4">
                                <h4 class="text-sm font-semibold text-gray-800">Companion</h4>
                                <UFormGroup label="Name">
                                    <UInput v-model="state.companion_name" type="text" size="lg" />
                                </UFormGroup>
                                <UFormGroup name="companion_phone" label="Phone">
                                    <InputPhone v-model="state.companion_phone" />
                                </UFormGroup>
                                <UFormGroup label="Relationship">
                                    <UInput v-model="state.companion_relationship" type="text" size="lg" placeholder="e.g. Spouse, Parent..." />
                                </UFormGroup>
                            </div>
                        </div>

                        <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                            <div class="px-5 py-4 space-y-4">
                                <h4 class="text-sm font-semibold text-gray-800">Medical</h4>
                                <UFormGroup label="Treating Doctor">
                                    <UInput v-model="state.treating_doctor" type="text" size="lg" placeholder="Dr..." />
                                </UFormGroup>
                                <UCheckbox v-model="state.has_medical_report" label="Has medical report" />
                            </div>
                        </div>

                        <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                            <div class="px-5 py-4 space-y-4">
                                <h4 class="text-sm font-semibold text-gray-800">Notes</h4>
                                <UFormGroup>
                                    <UTextarea v-model="state.notes" size="lg" :rows="3" placeholder="Additional information..." />
                                </UFormGroup>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </UForm>

        <UModal v-model="creatingCustomer" :ui="{ width: 'w-full sm:max-w-2xl' }">
            <UCard :ui="{ header: { background: 'bg-gray-50' }, footer: { background: 'bg-gray-50' } }">
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">New Customer</h3>
                        <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="creatingCustomer = false" />
                    </div>
                </template>
                <UForm ref="newCustomerForm" @submit="onCreateCustomer" :schema="newCustomerSchema" :state="newCustomerState" :validate-on="['submit']">
                    <div class="grid grid-cols-6 gap-x-4 gap-y-6">
                        <UFormGroup class="col-span-2" name="first_name" label="First Name" required>
                            <UInput v-model="newCustomerState.first_name" type="text" size="lg" />
                        </UFormGroup>
                        <UFormGroup class="col-span-2" name="middle_name" label="Father's Name" required>
                            <UInput v-model="newCustomerState.middle_name" type="text" size="lg" />
                        </UFormGroup>
                        <UFormGroup class="col-span-2" name="last_name" label="Last Name" required>
                            <UInput v-model="newCustomerState.last_name" type="text" size="lg" />
                        </UFormGroup>
                        <UFormGroup class="col-span-3" name="alternate_phone" label="Phone Number" required>
                            <InputPhone v-model="newCustomerState.alternate_phone" />
                        </UFormGroup>
                        <UFormGroup class="col-span-3" name="birth_year" label="Year of Birth" required>
                            <UInput v-model="newCustomerState.birth_year" type="number" size="lg" />
                        </UFormGroup>
                        <UFormGroup class="col-span-3" name="gender" label="Gender" required>
                            <USelectMenu v-model="newCustomerState.gender" :options="genders" value-attribute="value" size="lg" trailing />
                        </UFormGroup>
                        <UFormGroup class="col-span-3" name="blood_type" label="Blood Type">
                            <USelectMenu v-model="newCustomerState.blood_type" :options="bloodTypes" value-attribute="value" size="lg" trailing />
                        </UFormGroup>
                    </div>
                </UForm>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="creatingCustomer = false" size="lg" color="white">Cancel</UButton>
                        <UButton @click="newCustomerForm?.submit()" size="lg" color="black" :loading="savingCustomer">Create & Select</UButton>
                    </div>
                </template>
            </UCard>
        </UModal>
    </div>
</template>

<script setup lang="ts">
import { z } from 'zod'
import { normalizeStoredPhone, phoneSchema } from '~/utils/phone'
import type { FormSubmitEvent } from '#ui/types'
import { UserRole } from '~/types/user'
import { TransportReason, TransportPosture, TransportMobility, TransportTripType } from '~/types/transport'
import { Gender, BloodType, type Customer } from '~/types/customer'
import type { Provider, ProviderPlace, ProviderPricingZone } from '~/types/provider'
import type { PlacePrediction } from '~/composables/useGeocoding'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin, UserRole.medicalDirector, UserRole.doctor, UserRole.operator] })

const route = useRoute()
const editId = computed(() => route.query.id as string | undefined)
const isEditing = computed(() => !!editId.value)

useHead({ title: computed(() => isEditing.value ? 'Edit Transport' : 'New Transport') })

const client = useSupabaseClient()
const auth = useAuthStore()
const router = useRouter()
const toast = useToast()
const { age } = useNumber()
const { searchPredictions, getPlaceDetails, createSessionToken, getDrivingDistance, haversineDistance } = useGeocoding()

const form = ref()
const newCustomerForm = ref()
const loading = ref(false)
const providers = ref<Provider[]>([])

const customerSearch = ref('')
const searchingCustomers = ref(false)
const searchTimeout = ref<NodeJS.Timeout>()
const customerResults = ref<Customer[]>([])
const selectedCustomer = ref<Customer | null>(null)
const customerDropdownOpen = ref(false)
const creatingCustomer = ref(false)
const savingCustomer = ref(false)
const newCustomerState = ref({} as any)

const providerPlaces = ref<ProviderPlace[]>([])
const providerZones = ref<ProviderPricingZone[]>([])

const pickupSearch = ref('')
const searchingPickup = ref(false)
const pickupDropdownOpen = ref(false)
const pickupPlaces = ref<ProviderPlace[]>([])
const pickupPredictions = ref<PlacePrediction[]>([])
const pickupSelected = ref('')
const pickupLine1 = ref('')
const pickupSelectedPlace = ref<ProviderPlace | null>(null)
const pickupLat = ref<number | null>(null)
const pickupLng = ref<number | null>(null)
const pickupCity = ref<string | null>(null)
const pickupRegion = ref<string | null>(null)
const pickupCountry = ref<string | null>(null)
const pickupSessionToken = ref<string>('')

const dropoffSearch = ref('')
const searchingDropoff = ref(false)
const dropoffDropdownOpen = ref(false)
const dropoffPlaces = ref<ProviderPlace[]>([])
const dropoffPredictions = ref<PlacePrediction[]>([])
const dropoffSelected = ref('')
const dropoffLine1 = ref('')
const dropoffSelectedPlace = ref<ProviderPlace | null>(null)
const dropoffLat = ref<number | null>(null)
const dropoffLng = ref<number | null>(null)
const dropoffCity = ref<string | null>(null)
const dropoffRegion = ref<string | null>(null)
const dropoffCountry = ref<string | null>(null)
const dropoffSessionToken = ref<string>('')

const feeOverride = ref(false)
const manualFee = ref<number | null>(null)

const newCustomerSchema = z.object({
    first_name: z.string(),
    middle_name: z.string(),
    last_name: z.string(),
    alternate_phone: phoneSchema({ required: true }),
    birth_year: z.number().min(1900),
    gender: z.nativeEnum(Gender),
    blood_type: z.nativeEnum(BloodType).optional()
})

const genders = [
    { label: 'Male', value: Gender.male },
    { label: 'Female', value: Gender.female }
]

const bloodTypes = Object.values(BloodType).map((value) => ({ label: value, value }))

const addressSchema = z.object({
    formatted: z.string().min(1, 'Location is required'),
    lat: z.number({ required_error: 'Location is required' }),
    lng: z.number({ required_error: 'Location is required' }),
    provider_place_id: z.string().optional(),
    city: z.string().optional(),
    street: z.string().optional(),
    building: z.string().optional(),
    floor: z.string().optional(),
    narrow_stairs: z.boolean().optional(),
    no_electricity: z.boolean().optional(),
    no_elevator: z.boolean().optional(),
    name: z.string().optional(),
    department: z.string().optional(),
    room: z.string().optional(),
})

const schema = z.object({
    provider_id: z.string().min(1, 'Provider is required'),
    customer_id: z.string().min(1, 'Customer is required'),
    pickup_type: z.string().min(1),
    dropoff_type: z.string().min(1),
    companion_name: z.string().optional(),
    companion_phone: phoneSchema(),
    companion_relationship: z.string().optional(),
    reason: z.string().min(1, 'Reason is required'),
    reason_details: z.string().optional(),
    appointment_time: z.string().optional(),
    posture: z.string().optional(),
    mobility: z.string().optional(),
    trip_type: z.string().min(1, 'Trip type is required'),
    scheduled_date: z.string().min(1, 'Date is required'),
    scheduled_time: z.string().min(1, 'Time is required'),
    treating_doctor: z.string().optional(),
    has_medical_report: z.boolean(),
    departure_address: addressSchema,
    arrival_address: addressSchema,
    notes: z.string().optional(),
})

const state = ref({
    provider_id: '',
    customer_id: '',
    pickup_type: 'medical_facility',
    dropoff_type: 'residential',
    companion_name: '',
    companion_phone: '',
    companion_relationship: '',
    reason: '',
    reason_details: '',
    appointment_time: '',
    posture: '',
    mobility: '',
    trip_type: '',
    scheduled_date: '',
    scheduled_time: '',
    treating_doctor: '',
    has_medical_report: false,
    departure_address: { formatted: '', lat: undefined, lng: undefined } as any,
    arrival_address: { formatted: '', lat: undefined, lng: undefined } as any,
    notes: '',
})

const reasonOptions = [
    { value: TransportReason.returnHome, name: 'Return Home' },
    { value: TransportReason.assessmentAppointment, name: 'Assessment Appointment' },
    { value: TransportReason.medicalVisit, name: 'Medical Visit (Dialysis, ...)' },
    { value: TransportReason.hospitalAppointment, name: 'Hospital Appointment' },
]

const postureOptions = [
    { value: '', name: 'None' },
    { value: TransportPosture.walksAlone, name: 'Walks Alone' },
    { value: TransportPosture.needsCane, name: 'Needs a Cane' },
    { value: TransportPosture.needsHelp, name: 'Needs Help' },
]

const mobilityOptions = [
    { value: '', name: 'None' },
    { value: TransportMobility.wheelchair, name: 'Wheelchair' },
    { value: TransportMobility.bedridden, name: 'Completely Bedridden' },
]


const feeSource = ref<'place' | 'local' | 'zone' | null>(null)
const feeDistance = ref<number | null>(null)
const feeFirstLeg = ref<number | null>(null)
const feeZone = ref<ProviderPricingZone | null>(null)
const estimatedFee = ref<number | null>(null)
const calculatingFee = ref(false)

const loadingTransport = ref(false)

const feeReady = computed(() => {
    return !!(state.value.provider_id && state.value.trip_type && pickupLat.value && dropoffLat.value && !loadingTransport.value)
})

function isLocalAddress(lat: number, lng: number, city: string | null): boolean {
    const localPlaces = providerPlaces.value.filter(p => p.is_local_area)
    if (!localPlaces.length) return false

    if (city) {
        const match = localPlaces.some(p => p.address?.toLowerCase().includes(city.toLowerCase()))
        if (match) return true
    }

    const bases = providerPlaces.value.filter(p => p.is_base && p.lat != null && p.lng != null)
    if (bases.length) {
        const minDist = Math.min(...bases.map(b => haversineDistance(b.lat!, b.lng!, lat, lng)))
        if (minDist <= 3) return true
    }

    return false
}

watch([feeReady, feeOverride, () => state.value.trip_type, pickupLat, pickupLng, dropoffLat, dropoffLng, pickupSelectedPlace, dropoffSelectedPlace], async () => {
    if (feeOverride.value || !feeReady.value) {
        estimatedFee.value = null
        feeSource.value = null
        feeDistance.value = null
        feeFirstLeg.value = null
        feeZone.value = null
        return
    }

    calculatingFee.value = true

    const isRoundTrip = state.value.trip_type === TransportTripType.roundTrip
    const pLat = pickupLat.value!
    const pLng = pickupLng.value!
    const dLat = dropoffLat.value!
    const dLng = dropoffLng.value!
    const pCity = pickupCity.value
    const dCity = dropoffCity.value
    const pickupIsLocal = isLocalAddress(pLat, pLng, pCity)
    const dropoffIsLocal = isLocalAddress(dLat, dLng, dCity)

    if (pickupIsLocal && dropoffIsLocal) {
        const pickupPlace = pickupSelectedPlace.value
        const dropoffPlace = dropoffSelectedPlace.value
        if (pickupPlace?.one_way_price != null || dropoffPlace?.one_way_price != null) {
            const place = [pickupPlace, dropoffPlace].filter(p => p?.one_way_price != null).sort((a, b) => a!.one_way_price! - b!.one_way_price!)[0]!
            feeSource.value = 'place'
            feeDistance.value = null
        feeFirstLeg.value = null
            feeZone.value = null
            estimatedFee.value = isRoundTrip ? place.round_trip_price! : place.one_way_price!
            calculatingFee.value = false
            return
        }

        const localPlace = providerPlaces.value.find(p => p.is_local_area && p.one_way_price != null)
        if (localPlace) {
            feeSource.value = 'local'
            feeDistance.value = null
        feeFirstLeg.value = null
            feeZone.value = null
            estimatedFee.value = isRoundTrip ? localPlace.round_trip_price! : localPlace.one_way_price!
            calculatingFee.value = false
            return
        }
    }

    const bases = providerPlaces.value.filter(p => p.is_base && p.lat != null && p.lng != null)
    if (bases.length && providerZones.value.length) {
        const farLat = pickupIsLocal ? dLat : pLat
        const farLng = pickupIsLocal ? dLng : pLng

        const distances = await Promise.all(bases.map(b => getDrivingDistance(b.lat!, b.lng!, farLat, farLng)))
        const minDist = Math.min(...distances)
        feeDistance.value = minDist

        const zone = providerZones.value.find(z =>
            minDist >= z.min_km && (z.max_km == null || minDist < z.max_km)
        )

        if (zone) {
            let fee = isRoundTrip ? zone.round_trip_price : zone.one_way_price

            feeFirstLeg.value = null
            const pickupIsBase = pickupSelectedPlace.value?.is_base
            if (!pickupIsBase && bases.length) {
                const pickupDistances = await Promise.all(bases.map(b => getDrivingDistance(b.lat!, b.lng!, pLat, pLng)))
                const pickupDist = Math.min(...pickupDistances)
                const firstLegZone = providerZones.value.find(z =>
                    pickupDist >= z.min_km && (z.max_km == null || pickupDist < z.max_km)
                )
                if (firstLegZone?.first_leg_price) {
                    feeFirstLeg.value = firstLegZone.first_leg_price
                    fee += firstLegZone.first_leg_price
                }
            }

            feeSource.value = 'zone'
            feeZone.value = zone
            estimatedFee.value = fee
            calculatingFee.value = false
            return
        }
    }

    feeSource.value = null
    feeDistance.value = null
        feeFirstLeg.value = null
    feeZone.value = null
    estimatedFee.value = null
    calculatingFee.value = false
}, { immediate: true })

watch(() => state.value.provider_id, async (providerId) => {
    providerPlaces.value = []
    providerZones.value = []
    if (!providerId) return

    const [placesRes, zonesRes] = await Promise.all([
        client.from('provider_places').select('*').eq('provider_id', providerId),
        client.from('provider_pricing_zones').select('*').eq('provider_id', providerId).order('zone_number'),
    ])
    if (placesRes.data) providerPlaces.value = placesRes.data as any
    if (zonesRes.data) providerZones.value = zonesRes.data as any
})

let pickupTimeout: NodeJS.Timeout
watch(pickupSearch, (val) => {
    clearTimeout(pickupTimeout)
    if (loadingTransport.value) return
    if (val === pickupSelected.value) return
    if (!val || val.length < 3) { pickupPlaces.value = []; pickupPredictions.value = []; return }
    if (!pickupSessionToken.value) pickupSessionToken.value = createSessionToken()
    searchingPickup.value = true
    pickupTimeout = setTimeout(async () => {
        const [places, predictions] = await Promise.all([
            state.value.provider_id
                ? client.from('provider_places').select('*').eq('provider_id', state.value.provider_id).or(`name.ilike.%${val}%,address.ilike.%${val}%`).then(r => r.data || [])
                : Promise.resolve([]),
            searchPredictions(val, pickupSessionToken.value),
        ])
        pickupPlaces.value = places as any
        pickupPredictions.value = predictions
        searchingPickup.value = false
    }, 400)
})

let dropoffTimeout: NodeJS.Timeout
watch(dropoffSearch, (val) => {
    clearTimeout(dropoffTimeout)
    if (loadingTransport.value) return
    if (val === dropoffSelected.value) return
    if (!val || val.length < 3) { dropoffPlaces.value = []; dropoffPredictions.value = []; return }
    if (!dropoffSessionToken.value) dropoffSessionToken.value = createSessionToken()
    searchingDropoff.value = true
    dropoffTimeout = setTimeout(async () => {
        const [places, predictions] = await Promise.all([
            state.value.provider_id
                ? client.from('provider_places').select('*').eq('provider_id', state.value.provider_id).or(`name.ilike.%${val}%,address.ilike.%${val}%`).then(r => r.data || [])
                : Promise.resolve([]),
            searchPredictions(val, dropoffSessionToken.value),
        ])
        dropoffPlaces.value = places as any
        dropoffPredictions.value = predictions
        searchingDropoff.value = false
    }, 400)
})

function selectPickupPlace(place: ProviderPlace) {
    pickupSelectedPlace.value = place
    pickupSelected.value = place.address || place.name
    pickupLat.value = place.lat || null
    pickupLng.value = place.lng || null
    const city = place.address?.split(',')[0]?.trim() || null
    pickupCity.value = city
    pickupSearch.value = place.name
    pickupDropdownOpen.value = false
    state.value.departure_address = { ...state.value.departure_address, formatted: place.name, lat: place.lat, lng: place.lng, provider_place_id: place.id, city }
}

async function selectPickupPrediction(pred: PlacePrediction) {
    pickupDropdownOpen.value = false
    const details = await getPlaceDetails(pred.placeId, pickupSessionToken.value)
    pickupSessionToken.value = ''
    if (!details) return
    const displayText = pred.description || pred.mainText
    pickupSelectedPlace.value = null
    pickupSelected.value = displayText
    pickupLine1.value = pred.mainText || displayText
    pickupLat.value = details.lat
    pickupLng.value = details.lng
    const city = details.city || pred.secondaryText?.split(',')[0]?.trim() || null
    pickupCity.value = city
    pickupRegion.value = details.region || null
    pickupCountry.value = details.country || null
    pickupSearch.value = displayText
    pickupPredictions.value = []
    state.value.departure_address = { ...state.value.departure_address, formatted: displayText, lat: details.lat, lng: details.lng, city }
}

function selectDropoffPlace(place: ProviderPlace) {
    dropoffSelectedPlace.value = place
    dropoffSelected.value = place.address || place.name
    dropoffLat.value = place.lat || null
    dropoffLng.value = place.lng || null
    const city = place.address?.split(',')[0]?.trim() || null
    dropoffCity.value = city
    dropoffSearch.value = place.name
    dropoffDropdownOpen.value = false
    state.value.arrival_address = { ...state.value.arrival_address, formatted: place.name, lat: place.lat, lng: place.lng, provider_place_id: place.id, city }
}

function clearPickup() {
    pickupSelectedPlace.value = null
    pickupSelected.value = ''
    pickupLine1.value = ''
    pickupLat.value = null
    pickupLng.value = null
    pickupCity.value = null
    pickupRegion.value = null
    pickupCountry.value = null
    pickupSearch.value = ''
    pickupPredictions.value = []
    pickupPlaces.value = []
    pickupSessionToken.value = ''
    state.value.departure_address = { formatted: '', lat: undefined, lng: undefined, city: undefined, provider_place_id: undefined, name: undefined, department: undefined, room: undefined, street: undefined, building: undefined, floor: undefined } as any
}

function clearDropoff() {
    dropoffSelectedPlace.value = null
    dropoffSelected.value = ''
    dropoffLine1.value = ''
    dropoffLat.value = null
    dropoffLng.value = null
    dropoffCity.value = null
    dropoffRegion.value = null
    dropoffCountry.value = null
    dropoffSearch.value = ''
    dropoffPredictions.value = []
    dropoffPlaces.value = []
    dropoffSessionToken.value = ''
    state.value.arrival_address = { formatted: '', lat: undefined, lng: undefined, city: undefined, provider_place_id: undefined, name: undefined, department: undefined, room: undefined, street: undefined, building: undefined, floor: undefined } as any
}

async function selectDropoffPrediction(pred: PlacePrediction) {
    dropoffDropdownOpen.value = false
    const details = await getPlaceDetails(pred.placeId, dropoffSessionToken.value)
    dropoffSessionToken.value = ''
    if (!details) return
    const displayText = pred.description || pred.mainText
    dropoffSelectedPlace.value = null
    dropoffSelected.value = displayText
    dropoffLine1.value = pred.mainText || displayText
    dropoffLat.value = details.lat
    dropoffLng.value = details.lng
    const city = details.city || pred.secondaryText?.split(',')[0]?.trim() || null
    dropoffCity.value = city
    dropoffRegion.value = details.region || null
    dropoffCountry.value = details.country || null
    dropoffSearch.value = displayText
    dropoffPredictions.value = []
    state.value.arrival_address = { ...state.value.arrival_address, formatted: displayText, lat: details.lat, lng: details.lng, city }
}

watch(customerSearch, (value) => {
    if (!value || value.length < 2) { customerResults.value = []; return }
    searchCustomers()
})

watch(creatingCustomer, (open) => {
    if (open) {
        newCustomerState.value = {}
        if (customerSearch.value && /^\d+$/.test(customerSearch.value)) {
            newCustomerState.value.alternate_phone = normalizeStoredPhone(customerSearch.value)
        }
    }
})

onMounted(async () => {
    await fetchProviders()
    if (editId.value) await loadTransport()
})

async function loadTransport() {
    loadingTransport.value = true
    const { data } = await client.from('transports')
        .select(`
            *,
            customer:customers (id, first_name, middle_name, last_name, avatar, birth_date, gender, blood_type, phone),
            provider:providers (id, name, emails, avatar)
        `)
        .eq('id', editId.value!)
        .single()

    if (!data) return

    const t = data as any

    selectedCustomer.value = t.customer
    state.value = {
        provider_id: t.provider_id,
        customer_id: t.customer_id,
        pickup_type: t.pickup_type || 'medical_facility',
        dropoff_type: t.dropoff_type || 'residential',
        companion_name: t.companion_name || '',
        companion_phone: normalizeStoredPhone(t.companion_phone),
        companion_relationship: t.companion_relationship || '',
        reason: t.reason || '',
        reason_details: t.reason_details || '',
        appointment_time: t.appointment_time || '',
        posture: t.posture || '',
        mobility: t.mobility || '',
        trip_type: t.trip_type || '',
        scheduled_date: t.scheduled_date || '',
        scheduled_time: t.scheduled_time || '',
        treating_doctor: t.treating_doctor || '',
        has_medical_report: t.has_medical_report || false,
        departure_address: t.departure_address || {},
        arrival_address: t.arrival_address || {},
        notes: t.notes || '',
    }

    if (t.departure_address?.formatted) {
        pickupSearch.value = t.departure_address.formatted
        pickupSelected.value = t.departure_address.formatted
        pickupLine1.value = t.departure_address.formatted.split(',')[0]?.trim() || t.departure_address.formatted
        pickupLat.value = t.departure_address.lat || null
        pickupLng.value = t.departure_address.lng || null
        pickupCity.value = t.departure_address.city || null
    }

    if (t.arrival_address?.formatted) {
        dropoffSearch.value = t.arrival_address.formatted
        dropoffSelected.value = t.arrival_address.formatted
        dropoffLine1.value = t.arrival_address.formatted.split(',')[0]?.trim() || t.arrival_address.formatted
        dropoffLat.value = t.arrival_address.lat || null
        dropoffLng.value = t.arrival_address.lng || null
        dropoffCity.value = t.arrival_address.city || null
    }

    if (t.estimated_fee_override) {
        feeOverride.value = true
        manualFee.value = t.estimated_fee
    }

    if (t.provider_id) {
        const [placesRes, zonesRes] = await Promise.all([
            client.from('provider_places').select('*').eq('provider_id', t.provider_id),
            client.from('provider_pricing_zones').select('*').eq('provider_id', t.provider_id).order('zone_number'),
        ])
        if (placesRes.data) providerPlaces.value = placesRes.data as any
        if (zonesRes.data) providerZones.value = zonesRes.data as any
    }

    loadingTransport.value = false
}

async function fetchProviders() {
    const { data } = await client.from('providers').select('id, name, emails, avatar').is('deleted_at', null).order('name')
    if (data) providers.value = data as any
}

async function searchCustomers() {
    searchingCustomers.value = true
    if (searchTimeout.value) clearTimeout(searchTimeout.value)
    searchTimeout.value = setTimeout(async () => {
        const searchQuery = customerSearch.value.trim()
        const numberQuery = parseInt(customerSearch.value.replace(/[^\d]/g, ''))
        const { data } = await client.from('customers')
            .select(`id, number, user_id, subscription_id, first_name, middle_name, last_name, email, phone, avatar, birth_date, gender, blood_type, metadata, updated_at, parent:parent_id (id, first_name, middle_name, last_name), subscriptions (id, is_active, expires_at)`)
            .or([`fts.plfts(simple).'${searchQuery}'`, `email.ilike.%${searchQuery}%`, !isNaN(numberQuery) ? `phone.ilike.%${numberQuery}` : undefined, !isNaN(numberQuery) && numberQuery <= 2147483647 ? `number.eq.${numberQuery}` : undefined].filter(Boolean).join(','))
        if (data) customerResults.value = data as any
        searchingCustomers.value = false
    }, 600)
}

function onCustomerBlur() { customerDropdownOpen.value = false }
function selectCustomer(customer: Customer) { selectedCustomer.value = customer; state.value.customer_id = customer.id; customerSearch.value = ''; customerResults.value = []; customerDropdownOpen.value = false }
function clearCustomer() { selectedCustomer.value = null; state.value.customer_id = '' }

async function onCreateCustomer(event: FormSubmitEvent<z.infer<typeof newCustomerSchema>>) {
    savingCustomer.value = true
    const { data } = await client.from('customers').insert({ first_name: event.data.first_name, middle_name: event.data.middle_name, last_name: event.data.last_name, gender: event.data.gender, blood_type: event.data.blood_type, birth_date: event.data.birth_year ? `1-1-${event.data.birth_year}` : undefined, metadata: { alternate_phone: event.data.alternate_phone } } as never).select('id, first_name, middle_name, last_name, avatar, birth_date, gender, blood_type').single()
    if (data) { selectCustomer(data as any); creatingCustomer.value = false; toast.add({ title: 'Customer created.', color: 'green', icon: 'i-ph-check-circle-duotone' }) }
    savingCustomer.value = false
}

async function onSubmit(event: FormSubmitEvent<z.infer<typeof schema>>) {
    loading.value = true

    const fee = feeOverride.value ? manualFee.value : estimatedFee.value

    const payload: any = {
        provider_id: event.data.provider_id,
        customer_id: event.data.customer_id,
        created_by: auth.user?.id,
        pickup_type: event.data.pickup_type,
        dropoff_type: event.data.dropoff_type,
        trip_type: event.data.trip_type,
        scheduled_date: event.data.scheduled_date,
        scheduled_time: event.data.scheduled_time,
        reason: event.data.reason,
        has_medical_report: event.data.has_medical_report,
        estimated_fee: fee,
        estimated_fee_override: feeOverride.value,
    }

    if (event.data.reason_details) payload.reason_details = event.data.reason_details
    if (event.data.appointment_time) payload.appointment_time = event.data.appointment_time
    if (event.data.posture) payload.posture = event.data.posture
    if (event.data.mobility) payload.mobility = event.data.mobility
    if (event.data.treating_doctor) payload.treating_doctor = event.data.treating_doctor
    if (event.data.companion_name) payload.companion_name = event.data.companion_name
    if (event.data.companion_phone) payload.companion_phone = event.data.companion_phone
    if (event.data.companion_relationship) payload.companion_relationship = event.data.companion_relationship
    if (event.data.notes) payload.notes = event.data.notes

    if (event.data.pickup_type === 'medical_facility') {
        payload.origin_hospital = event.data.departure_address.name || null
        payload.origin_department = event.data.departure_address.department || null
        payload.origin_room = event.data.departure_address.room || null
    }

    const cleanAddress = (addr: any) => {
        if (!addr) return null
        const cleaned = Object.fromEntries(Object.entries(addr).filter(([_, v]) => v != null && v !== ''))
        return Object.keys(cleaned).length ? cleaned : null
    }

    payload.departure_address = cleanAddress(event.data.departure_address)
    payload.arrival_address = cleanAddress(event.data.arrival_address)

    let data, error

    if (isEditing.value) {
        delete payload.created_by
        const result = await client.from('transports').update(payload as never).eq('id', editId.value!).select()
        data = result.data
        error = result.error
    } else {
        const result = await client.from('transports').insert(payload as never).select()
        data = result.data
        error = result.error
    }

    if (data) {
        toast.add({ title: isEditing.value ? 'Transport updated.' : 'Transport created successfully.', color: 'green', icon: 'i-ph-check-circle-duotone' })
        router.push('/transports')
    } else if (error) {
        console.error(error)
        toast.add({ title: 'Could not create transport, please try again.', color: 'red', icon: 'i-ph-warning-duotone' })
    }

    loading.value = false
}
</script>
